import { useEffect } from 'react';
import { AuthProvider } from './shared/contexts/AuthContext';
import telemetry from './shared/utils/telemetry';
import { Provider } from 'react-redux';
import { store } from './app/store';
import AuthInitializer from './app/components/AuthInitializer';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './core/providers/queryClient';
import { Auth0Provider, type AppState } from '@auth0/auth0-react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AppRoutes from './core/routes/AppRoutes';
import { SignalRProvider } from './shared/contexts/SignalRContext';
import { NotificationListener } from './shared/components/notifications/NotificationListener';
import { SenderDetailsDialogWrapper } from './shared/components/ui/Dialog/SenderDetailsDialogWrapper';
import ToastContainer from './shared/components/ui/Toast/ToastContainer';
import { LocalizationProvider as SkyporterLocalizationProvider } from './shared/contexts/LocalizationContext';
import { CartUpdatesProvider } from './features/Sender/contexts/CartUpdatesContext';
import TimezoneChangeNotification from './shared/components/ui/TimezoneChangeNotification/TimezoneChangeNotification';

// Detect if localStorage is available (blocked in some incognito/private browsing configurations)
const getAuth0CacheLocation = (): 'localstorage' | 'memory' => {
  try {
    localStorage.setItem('__storage_test__', '1');
    localStorage.removeItem('__storage_test__');
    return 'localstorage';
  } catch {
    return 'memory';
  }
};

// Inner component that has access to React Router's navigate
const Auth0ProviderWithNavigate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo || window.location.pathname, { replace: true });
  };

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      cacheLocation={getAuth0CacheLocation()}
      useRefreshTokens={true}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

// Receiver pages are a guest flow: they must only call receiver APIs, so they skip the
// Sender/Traveller app shell (profile/roles/weight-unit fetches, missed notifications,
// SignalR notification hub, cart updates, offer dialogs).
const AppShell: React.FC = () => {
  const { pathname } = useLocation();

  if (pathname.startsWith('/receiver')) {
    // AuthProvider makes no API calls; it's kept because ToastContainer reads useAuth().
    return (
      <AuthProvider>
        <AppRoutes />
        <ToastContainer />
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <CartUpdatesProvider>
        <SignalRProvider>
          <AuthInitializer>
            <TimezoneChangeNotification />
            <AppRoutes />
            <NotificationListener />
            <SenderDetailsDialogWrapper />
            <ToastContainer />
          </AuthInitializer>
        </SignalRProvider>
      </CartUpdatesProvider>
    </AuthProvider>
  );
};

function App() {
  useEffect(() => {
    telemetry.initializeAppInsights();
    telemetry.checkAppVersion();

    return () => {
      telemetry.cleanup();
    };
  }, []);

  return (
    <BrowserRouter>
      <Auth0ProviderWithNavigate>
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <SkyporterLocalizationProvider>
                <AppShell />
              </SkyporterLocalizationProvider>
            </LocalizationProvider>
          </QueryClientProvider>
        </Provider>
      </Auth0ProviderWithNavigate>
    </BrowserRouter>
  );
}

export default App;
